// ---------------------------------------------------------------------------
// TemplateProposalCard — pure UI for the template-proposal chat card (Plan
// B1 Task 4). House style copied from
// ../../chat/components/terminology-selection-card.tsx (rounded-lg border
// bg-card shell, header/body/footer sections, data-testid'd buttons).
//
// This component is PURE UI — it takes displayName/matchedTerms plus
// injected onAccept/onDecline callbacks and knows nothing about the store,
// the server, template instantiation, or free generation. Task 4's
// chat-messages.tsx render-switch case wires those.
// ---------------------------------------------------------------------------

import { useStudioLocale } from '@/i18n'

interface TemplateProposalCardProps {
  displayName: string
  matchedTerms: string[]
  onAccept: () => void
  onDecline: () => void
  /**
   * True while an accept is in flight (instantiateTemplateToProject awaiting
   * its result). Disables BOTH buttons — Accept must not be re-entrant
   * (double-click would create two projects), and Decline racing an
   * in-flight accept could fire free-generation on top of a
   * still-resolving/about-to-succeed instantiation. Defaults to false so
   * every other call site (and existing tests) is unaffected.
   */
  busy?: boolean
}

export function TemplateProposalCard({
  displayName,
  matchedTerms,
  onAccept,
  onDecline,
  busy = false,
}: TemplateProposalCardProps) {
  const { strings } = useStudioLocale()
  const copy = strings.chat.templateProposal

  return (
    <div className="my-2 rounded-lg border bg-card" data-testid="template-proposal-card">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/50">
        <span className="text-sm font-semibold">{copy.heading}</span>
      </div>

      {/* Body */}
      <div className="p-3 space-y-2">
        <div className="text-sm">
          <span className="font-medium">{displayName}</span>{' '}
          {copy.startFaster}
        </div>
        {matchedTerms.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {matchedTerms.map((term) => (
              <span
                key={term}
                className="rounded border bg-background px-2 py-1 text-xs text-muted-foreground"
                data-testid="template-proposal-matched-term"
              >
                {term}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center gap-2 px-4 py-2 border-t bg-muted/30">
        <button
          type="button"
          className="px-3 py-1 text-sm font-medium rounded border hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
          onClick={onAccept}
          disabled={busy}
          data-testid="template-proposal-accept"
        >
          {busy ? copy.accepting : copy.accept}
        </button>
        <button
          type="button"
          className="px-3 py-1 text-sm font-medium rounded border hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
          onClick={onDecline}
          disabled={busy}
          data-testid="template-proposal-decline"
        >
          {copy.decline}
        </button>
      </div>
    </div>
  )
}
